import { displayFileName } from "./fileNames";
import { matchMaybe } from "./maybe";
import { sceneListName, unknownOpening, type SceneOpening } from "./sceneLabel";
import {
  chapterFolderFromScenePath,
  sceneFileFromScenePath,
  scenePath,
  type ZwChapter,
} from "./zwConfig";

export type ManuscriptSceneOption = {
  value: string;
  label: string;
};

/** Optional body-opening lookup keyed by `Chapters/<folder>/<scene>`. */
export type ManuscriptSceneOpeningFor = (path: string) => SceneOpening;

/** Select value for an arc moment with no manuscript link. */
export const MANUSCRIPT_SCENE_NONE = "";

/** Flat manuscript order — chapter label · scene label, for arc moment pickers. */
export function manuscriptSceneSelectOptions(
  chapters: ZwChapter[],
  openingFor?: ManuscriptSceneOpeningFor,
): ManuscriptSceneOption[] {
  const options: ManuscriptSceneOption[] = [
    { value: MANUSCRIPT_SCENE_NONE, label: "None" },
  ];
  for (const chapter of chapters) {
    const chapterLabel = chapter.name;
    chapter.scenes.forEach((scene) => {
      const path = scenePath(chapter.folder, scene.file);
      const opening = openingFor?.(path) ?? unknownOpening;
      options.push({
        value: path,
        label: `${chapterLabel} · ${sceneListName(scene, opening)}`,
      });
    });
  }
  return options;
}

/** Human label for a `Chapters/<folder>/<scene>` path. */
export function manuscriptSceneLabel(
  path: string,
  chapters: ZwChapter[],
  openingFor?: ManuscriptSceneOpeningFor,
): string {
  const folderM = chapterFolderFromScenePath(path);
  const sceneFileM = sceneFileFromScenePath(path);
  return matchMaybe(folderM, {
    nothing: () => displayFileName(path),
    just: (folder) =>
      matchMaybe(sceneFileM, {
        nothing: () => displayFileName(path),
        just: (sceneFile) => {
          if (folder.length === 0 || sceneFile.length === 0) {
            return displayFileName(path);
          }
          const chapter = chapters.find((entry) => entry.folder === folder);
          if (typeof chapter === "undefined") return displayFileName(sceneFile);
          const index = chapter.scenes.findIndex(
            (scene) => scene.file === sceneFile,
          );
          if (index < 0) return displayFileName(sceneFile);
          const scene = chapter.scenes[index]!;
          const opening = openingFor?.(path) ?? unknownOpening;
          return `${chapter.name} · ${sceneListName(scene, opening)}`;
        },
      }),
  });
}
